import {Check} from '@blesk-frontend/ui';
import {
  browserProfilingIntegration,
  init,
  replayIntegration,
  tanstackRouterBrowserTracingIntegration,
} from '@sentry/react';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {createRouter, RouterProvider} from '@tanstack/react-router';
import axios from 'axios';
import {Toaster} from 'sonner';

import {ThemeProvider, YandexMetrikaProvider} from '@/providers';
import {routeTree} from './routeTree.gen';

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const queryClient = new QueryClient();

const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
});

if (import.meta.env.PROD) {
  try {
    init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [
        browserProfilingIntegration(),
        tanstackRouterBrowserTracingIntegration(router),
        replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      tracesSampleRate: 1.0,
      tracePropagationTargets: ['localhost', import.meta.env.VITE_API_END_POINT as string],
      profilesSampleRate: 1.0,
      replaysSessionSampleRate: 0.5,
      replaysOnErrorSampleRate: 1.0,
    });
  } catch (e) {
    console.error(e);
  }
}

axios.defaults.baseURL = import.meta.env.VITE_API_END_POINT;

export const App = () => (
  <>
    <ThemeProvider storageKey="@blesk/ui-theme">
      <Toaster
        theme="light"
        richColors={false}
        icons={{success: <Check />}}
        toastOptions={{
          classNames: {
            success: '[&>*]:data-[icon]:!text-success-primary',
            error: '[&>*]:data-[icon]:!text-error-primary',
            title: '!text-paragraph-sm !text-text-primary !font-normal',
          },
        }}
      />
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ThemeProvider>
    <YandexMetrikaProvider />
  </>
);
